// src/components/GardenLogBook.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  Chip,
  IconButton,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import apiClient from "../utils/apiClient";
import { formatDate } from "../utils/dateUtils";

const GardenLogBook = ({ garden_uuid, onEditEvent, refreshKey, year }) => {
  console.log("🧭 GardenLogBook render year =", year);

  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [expandedEvents, setExpandedEvents] = useState(new Set());

  const fetchEvents = async () => {
    try {
      const response = await apiClient.get(`/gardens/${garden_uuid}/events`);
      setAllEvents(response.data || []);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [garden_uuid, refreshKey]);

  useEffect(() => {
    if (!allEvents?.length) {
      setFilteredEvents([]);
      return;
    }

    const filtered = allEvents.filter((event) => {
      const ws = event.window_start ? new Date(event.window_start) : null;
      const we = event.window_end ? new Date(event.window_end) : null;
      const de = event.date_executed ? new Date(event.date_executed) : null;

      if (!ws && !we && !de) return false;

      const startYear = ws?.getFullYear();
      const endYear = we?.getFullYear();
      const execYear = de?.getFullYear();

      return (
        startYear === year ||
        endYear === year ||
        execYear === year ||
        (startYear && endYear && startYear < year && endYear > year)
      );
    });

    filtered.sort((a, b) => {
      const da = a.window_start ? new Date(a.window_start) : new Date(0);
      const db = b.window_start ? new Date(b.window_start) : new Date(0);
      return da - db;
    });

    console.log(`🎯 Filtered ${filtered.length} events for year ${year}`);
    setFilteredEvents(filtered);
  }, [allEvents, year]);

  const completedEvents = filteredEvents.filter(event => event.status === "done");
  const upcomingEvents = filteredEvents.filter(event => event.status === "planned");
  const skippedEvents = filteredEvents.filter(event => event.status === "skipped");
  const completedAndSkippedEvents = [...completedEvents, ...skippedEvents];

  const toggleEvent = (eventUuid) => {
    setExpandedEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventUuid)) {
        newSet.delete(eventUuid);
      } else {
        newSet.add(eventUuid);
      }
      return newSet;
    });
  };

  const collapseAll = () => {
    setExpandedEvents(new Set());
  };

  const expandAll = () => {
    const allEventUuids = new Set([...completedAndSkippedEvents, ...upcomingEvents].map(e => e.event_uuid));
    setExpandedEvents(allEventUuids);
  };

  const getWindowRange = (event) => {
    if (event.window_start && event.window_end) {
      const start = new Date(event.window_start);
      const end = new Date(event.window_end);
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    }
    return "No window";
  };

  const formatEventDate = (event) => {
    if (event.date_executed) {
      return formatDate(event.date_executed);
    }
    return getWindowRange(event);
  };

  const getEventTypeColor = (status) => {
    if (status === "done") return "#8b4513"; // Brown for done
    if (status === "skipped") return "#808080"; // Gray for skipped
    return "#d2691e"; // Orange for planned
  };

  const getPlotName = (event) => {
    return event.plot?.name ? `Bed ${event.plot.name}` : "Unknown Plot";
  };

  const isWithinWindow = (event) => {
    if (!event.window_start || !event.window_end) return false;
    const now = new Date();
    const start = new Date(event.window_start);
    const end = new Date(event.window_end);
    return now >= start && now <= end;
  };

  const getCropName = (event) => {
    if (event.planted?.crop?.name) return event.planted.crop.name;
    const notes = event.notes || "";
    if (notes.toLowerCase().startsWith("sow cover crop:")) {
      const name = notes.slice("sow cover crop:".length).trim();
      if (name) return name;
    }
    return "Unknown Crop";
  };

  const getEventAmount = (event) => {
    return event.amount ?? null;
  };

  if (!filteredEvents?.length) {
    return (
      <Paper sx={{
        p: 3,
        background: 'transparent',
        border: '2px solid var(--color-border-vintage)',
        borderRadius: 0,
        boxShadow: 'none',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
        }}></div>
        
        <Typography variant="body1" sx={{
          fontFamily: 'var(--font-serif)',
          color: 'var(--color-ink)',
          lineHeight: 1.6,
          mt: 2,
          textAlign: 'center'
        }}>
          No activities recorded for {year}. Start by adding your first garden task or observation!
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Completed & Skipped Events Section */}
      {completedAndSkippedEvents.length > 0 && (
        <Paper sx={{
          p: 3,
          background: 'transparent',
          border: '2px solid var(--color-border-vintage)',
          borderRadius: 0,
          boxShadow: 'none',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
          }}></div>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
            <Typography variant="h5" sx={{
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              textAlign: 'center'
            }}>
              Completed Activities
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={expandAll}
                sx={{
                  fontFamily: 'var(--font-serif)',
                  color: 'var(--color-sepia)',
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Expand All
              </Button>
              <Button
                size="small"
                onClick={collapseAll}
                sx={{
                  fontFamily: 'var(--font-serif)',
                  color: 'var(--color-sepia)',
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Collapse All
              </Button>
            </Box>
          </Box>

          {completedAndSkippedEvents.map((event, index) => {
            const isExpanded = expandedEvents.has(event.event_uuid);
            return (
              <Box key={event.event_uuid}>
                {/* Event Header - Always Visible */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 1,
                    cursor: 'pointer'
                  }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEvent(event.event_uuid);
                  }}
                >
                  <Chip
                    label={event.event_type?.name || 'Unknown Task'}
                    size="small"
                    sx={{
                      backgroundColor: getEventTypeColor(event.status),
                      color: 'white',
                      fontFamily: 'var(--font-serif)',
                      fontWeight: 600
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      color: 'var(--color-sepia)',
                      fontStyle: 'italic'
                    }}
                  >
                    {getCropName(event)} - {getPlotName(event)}{getEventAmount(event) ? ` - ${getEventAmount(event)} pcs.` : ""}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEvent(event.event_uuid);
                    }}
                    sx={{ ml: 'auto', color: 'var(--color-sepia)' }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Event Details - Collapsible */}
                {isExpanded && (
                  <>
                    <Divider sx={{ mb: 2, borderColor: 'var(--color-border-vintage)' }} />
                    <Box sx={{ ml: 2, mb: 3, cursor: 'pointer' }} onClick={() => onEditEvent(event)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)',
                            fontWeight: 500
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Schedule window:
                          </span>{" "}
                          {getWindowRange(event)}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)'
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Amount:
                          </span>{" "}
                          {getEventAmount(event) ?? "-"}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)',
                            fontStyle: 'italic',
                            mt: 1
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Notes:
                          </span>{" "}
                          {event.notes || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {index < completedAndSkippedEvents.length - 1 && (
                  <Divider sx={{ mb: 2, borderColor: 'rgba(184, 168, 138, 0.3)' }} />
                )}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <Paper sx={{
          p: 3,
          background: 'transparent',
          border: '2px solid var(--color-border-vintage)',
          borderRadius: 0,
          boxShadow: 'none',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, var(--color-accent-gold) 0%, transparent 50%, var(--color-accent-gold) 100%)'
          }}></div>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2 }}>
            <Typography variant="h5" sx={{
              fontFamily: 'var(--font-decorative)',
              color: 'var(--color-sepia)',
              textAlign: 'center'
            }}>
              Upcoming Tasks
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={expandAll}
                sx={{
                  fontFamily: 'var(--font-serif)',
                  color: 'var(--color-sepia)',
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Expand All
              </Button>
              <Button
                size="small"
                onClick={collapseAll}
                sx={{
                  fontFamily: 'var(--font-serif)',
                  color: 'var(--color-sepia)',
                  textTransform: 'none',
                  fontSize: '0.75rem'
                }}
              >
                Collapse All
              </Button>
            </Box>
          </Box>

          {upcomingEvents.map((event, index) => {
            const withinWindow = isWithinWindow(event);
            const isExpanded = expandedEvents.has(event.event_uuid);
            return (
              <Box 
                key={event.event_uuid}
                sx={{ 
                  ...(withinWindow && {
                    backgroundColor: 'rgba(201, 169, 97, 0.1)',
                    border: '2px solid var(--color-accent-gold)',
                    borderRadius: '4px',
                    padding: '1rem',
                    margin: '0.5rem 0'
                  })
                }}
              >
                {/* Event Header - Always Visible */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 1,
                    cursor: 'pointer'
                  }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleEvent(event.event_uuid);
                  }}
                >
                  <Chip
                    label={event.event_type?.name || 'Unknown Task'}
                    size="small"
                    sx={{
                      backgroundColor: withinWindow ? '#d2691e' : getEventTypeColor(event.status),
                      color: 'white',
                      fontFamily: 'var(--font-serif)',
                      fontWeight: 600,
                      ...(withinWindow && {
                        boxShadow: '0 0 8px rgba(210, 105, 30, 0.5)'
                      })
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      color: withinWindow ? 'var(--color-accent-gold)' : 'var(--color-sepia)',
                      fontStyle: 'italic',
                      fontWeight: withinWindow ? 600 : 400
                    }}
                  >
                    {getCropName(event)} - {getPlotName(event)}{getEventAmount(event) ? ` - ${getEventAmount(event)} pcs.` : ""}
                  </Typography>
                  {withinWindow && (
                    <Typography variant="body2" sx={{
                      fontFamily: 'var(--font-serif)',
                      color: 'var(--color-accent-gold)',
                      fontWeight: 600,
                      ml: 'auto'
                    }}>
                      ⏰ Now Due
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleEvent(event.event_uuid);
                    }}
                    sx={{ ml: withinWindow ? 0 : 'auto', color: 'var(--color-sepia)' }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* Event Details - Collapsible */}
                {isExpanded && (
                  <>
                    <Divider sx={{ mb: 2, borderColor: 'var(--color-border-vintage)' }} />
                    <Box sx={{ ml: 2, mb: 3, cursor: 'pointer' }} onClick={() => onEditEvent(event)}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)',
                            fontWeight: 500
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Schedule window:
                          </span>{" "}
                          {getWindowRange(event)}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)'
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Amount:
                          </span>{" "}
                          {getEventAmount(event) ?? "-"}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            color: 'var(--color-ink)',
                            fontStyle: 'italic',
                            mt: 1
                          }}
                        >
                          <span style={{ color: 'var(--color-sepia)', fontWeight: 600 }}>
                            Notes:
                          </span>{" "}
                          {event.notes || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {index < upcomingEvents.length - 1 && (
                  <Divider sx={{ mb: 2, borderColor: 'rgba(184, 168, 138, 0.3)' }} />
                )}
              </Box>
            );
          })}
        </Paper>
      )}

    </Box>
  );
};

export default GardenLogBook;
